import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CrossDeviceNudge } from "@/components/CrossDeviceNudge";
import api from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PulseBar } from "@/components/home/PulseBar";

import Home from "@/pages/Home";
import HomeV2 from "@/pages/HomeV2";
import CategoryPage from "@/pages/CategoryPage";
import CryptoSectorPage from "@/pages/CryptoSectorPage";
import DeFiSectorPage from "@/pages/DeFiSectorPage";
import DeFiProtocolsPage from "@/pages/DeFiProtocolsPage";
import PolicySectorPage from "@/pages/PolicySectorPage";
import ResearchPage from "@/pages/ResearchPage";
import GuidesPage from "@/pages/GuidesPage";
import ArticlePage from "@/pages/ArticlePage";
import RegulationTracker from "@/pages/RegulationTracker";
import RegulationDetail from "@/pages/RegulationDetail";
import { RegulationIntelligenceSection } from "@/components/home/RegulationIntelligenceSection";
import SearchPage from "@/pages/SearchPage";
import EntitiesPage from "@/pages/EntitiesPage";
import AuthorPage from "@/pages/AuthorPage";
import Advertise from "@/pages/Advertise";
import PreloaderPreview, { PreloaderDemo } from "@/pages/PreloaderPreview";
import HeaderPreview from "@/pages/HeaderPreview";
import NotFound from "@/pages/NotFound";
import { InstallPrompt } from "@/components/InstallPrompt";
import { TelegramPrompt } from "@/components/TelegramPrompt";
import { NewsletterPopup } from "@/components/NewsletterPopup";
import PRService from "@/pages/PRService";
import Premium from "@/pages/Premium";
import AuthorProgram from "@/pages/AuthorProgram";
import AuthorStudio from "@/pages/AuthorStudio";
import AuthorsPage from "@/pages/AuthorsPage";
import MyFeed from "@/pages/MyFeed";
import CoinPage from "@/pages/CoinPage";
import JurisdictionProfile from "@/pages/JurisdictionProfile";
import ProtocolPage from "@/pages/ProtocolPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Account from "@/pages/Account";
import ChangePassword from "@/pages/ChangePassword";
import ResetPassword from "@/pages/ResetPassword";
import ForgotPassword from "@/pages/ForgotPassword";
import BriefArchive from "@/pages/BriefArchive";
import LayerIndexPage from "@/pages/LayerIndexPage";
import Originals from "@/pages/Originals";
import About from "@/pages/About";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import RadarArchive from "@/pages/RadarArchive";

import AdminLayout from "@/admin/AdminLayout";
import Dashboard from "@/admin/Dashboard";
import Sources from "@/admin/Sources";
import Runs from "@/admin/Runs";
import ReviewQueue from "@/admin/ReviewQueue";
import Articles from "@/admin/Articles";
import Regulations from "@/admin/Regulations";
import HomepageManager from "@/admin/HomepageManager";
import SectorsAdmin from "@/admin/Sectors";
import Newsletters from "@/admin/Newsletters";
import Social from "@/admin/Social";
import Leads from "@/admin/Leads";
import Analytics from "@/admin/Analytics";
import Traffic from "@/admin/Traffic";
import Revenue from "@/admin/Revenue";
import Payouts from "@/admin/Payouts";
import Users from "@/admin/Users";
import Settings from "@/admin/Settings";
import AuditLogs from "@/admin/AuditLogs";
import Ads from "@/admin/Ads";
import AdminPredictions from "@/admin/Predictions";
import AdOrders from "@/admin/AdOrders";
import AdPricing from "@/admin/AdPricing";
import PRSubmissions from "@/admin/PRSubmissions";
import Authors from "@/admin/Authors";
import SeoHealth from "@/admin/SeoHealth";
import Intelligence from "@/admin/Intelligence";
import WriteAsAuthor from "@/admin/WriteAsAuthor";
import { RouteSeo } from "@/components/Seo";
import { LanguageProvider, useLang, SUPPORTED_LANGS } from "@/lib/i18n";
import { PullToRefresh } from "@/components/PullToRefresh";
import { BackToTop } from "@/components/BackToTop";

const CATEGORY_PATHS = ["/latest", "/web3", "/blockchain",
  "/markets", "/ai", "/quantum"];

/* One canonical Policy destination: permanent client redirect, query preserved */
const LegacyPolicyRedirect = () => {
  const location = useLocation();
  return <Navigate to={{ pathname: "/policy", search: location.search }} replace />;
};

const rid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
const stored = (store: Storage, key: string) => {
  try {
    let v = store.getItem(key);
    if (!v) { v = rid(); store.setItem(key, v); }
    return v;
  } catch { return "na"; }
};
const deviceType = () => {
  const ua = navigator.userAgent;
  if (/iPad|Tablet|PlayBook|Silk/i.test(ua)) return "tablet";
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return "mobile";
  return "desktop";
};

const PageTracker = () => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!location.pathname.startsWith("/admin")) {
      const meta: any = {
        path: location.pathname,
        vid: stored(localStorage, "nl_vid"),
        sid: stored(sessionStorage, "nl_sid"),
        device: deviceType(),
      };
      if (!(window as any).__nl_landed) {
        (window as any).__nl_landed = true;
        if (document.referrer && !document.referrer.includes(window.location.hostname)) {
          meta.referrer = document.referrer;
        }
        const utm = new URLSearchParams(window.location.search).get("utm_source");
        if (utm) meta.utm_source = utm;
      }
      api.post("/analytics/track", { event_type: "page_view", metadata: meta }).catch(() => {});
    }
  }, [location.pathname]);
  return null;
};

const LangUrlSync = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang, setLang } = useLang();
  const langRef = useRef(lang);
  langRef.current = lang;
  useEffect(() => {
    const m = location.pathname.match(/^\/([a-z]{2})\/article\//);
    const urlL = m?.[1];
    if (!urlL) return;
    if (!SUPPORTED_LANGS.includes(urlL)) {
      navigate(location.pathname.replace(/^\/[a-z]{2}/, ""), { replace: true });
    } else if (urlL !== langRef.current) {
      setLang(urlL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  return null;
};

const PublicLayout = () => {
  const { lang } = useLang();
  const { pathname } = useLocation();
  return (
  <div className="min-h-screen bg-[#000000] flex flex-col">
    <RouteSeo />
    <PullToRefresh />
    {/* NewStoriesToast unmounted per Founder MI fidelity directive (non-Figma element); component kept for rollback */}
    <Navbar />
    {/* V2 preview carries its own pulse band (Figma composition) */}
    {pathname !== "/home-v2" && <PulseBar />}
    <main className="flex-1" key={lang}><Outlet /></main>
    <BackToTop />
    <Footer />
  </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <LanguageProvider>
        <PageTracker />
        <LangUrlSync />
        <CrossDeviceNudge />
        <InstallPrompt />
        <TelegramPrompt />
        <NewsletterPopup />
        <Toaster position="bottom-right" theme="dark" />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            {/* Homepage V2 preview (Phase 2 shell) — production cutover gated */}
            <Route path="/home-v2" element={<HomeV2 />} />
            {CATEGORY_PATHS.map((p) => <Route key={p} path={p} element={<CategoryPage />} />)}
            <Route path="/crypto" element={<CryptoSectorPage />} />
            <Route path="/defi" element={<DeFiSectorPage />} />
            <Route path="/defi/protocols" element={<DeFiProtocolsPage />} />
            <Route path="/policy" element={<PolicySectorPage />} />
            <Route path="/research" element={<ResearchPage />} />
            <Route path="/explainers" element={<GuidesPage />} />
            <Route path="/government-policy" element={<LegacyPolicyRedirect />} />
            <Route path="/article/:slug" element={<ArticlePage />} />
            <Route path="/:urlLang/article/:slug" element={<ArticlePage />} />
            <Route path="/regulation-tracker" element={<RegulationTracker />} />
            <Route path="/jurisdiction/:code" element={<JurisdictionProfile />} />
            <Route path="/briefs" element={<BriefArchive />} />
            <Route path="/layer-index" element={<LayerIndexPage />} />
            <Route path="/originals" element={<Originals />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/radar" element={<RadarArchive />} />
            <Route path="/regulation/:slug" element={<RegulationDetail />} />
            <Route path="/author/:slug" element={<AuthorPage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/entities" element={<EntitiesPage />} />
            <Route path="/advertise" element={<Advertise />} />
            <Route path="/preloader-preview" element={<PreloaderPreview />} />
            <Route path="/header-preview" element={<HeaderPreview />} />
            <Route path="/preloader-preview/:option" element={<PreloaderDemo />} />

            <Route path="/pr" element={<PRService />} />
            <Route path="/premium" element={<Premium />} />
            <Route path="/author-program" element={<AuthorProgram />} />
            <Route path="/authors" element={<AuthorsPage />} />
            <Route path="/studio" element={<AuthorStudio />} />
            <Route path="/my-feed" element={<MyFeed />} />
            <Route path="/coin/:symbol" element={<CoinPage />} />
            <Route path="/protocols/:slug" element={<ProtocolPage />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/account" element={<Account />} />
            <Route path="/change-password" element={<ChangePassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="/admin/login" element={<div className="min-h-screen bg-[#000000]"><Login /></div>} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="sources" element={<Sources />} />
            <Route path="runs" element={<Runs />} />
            <Route path="review-queue" element={<ReviewQueue />} />
            <Route path="articles" element={<Articles />} />
            <Route path="regulations" element={<Regulations />} />
            <Route path="homepage" element={<HomepageManager />} />
            <Route path="sectors" element={<SectorsAdmin />} />
            <Route path="newsletters" element={<Newsletters />} />
            <Route path="social" element={<Social />} />
            <Route path="leads" element={<Leads />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="traffic" element={<Traffic />} />
            <Route path="seo" element={<SeoHealth />} />
            <Route path="intelligence" element={<Intelligence />} />
            <Route path="revenue" element={<Revenue />} />
            <Route path="payouts" element={<Payouts />} />
            <Route path="users" element={<Users />} />
            <Route path="settings" element={<Settings />} />
            <Route path="audit-logs" element={<AuditLogs />} />
            <Route path="ads" element={<Ads />} />
            <Route path="predictions" element={<AdminPredictions />} />
            <Route path="ad-orders" element={<AdOrders />} />
            <Route path="ad-pricing" element={<AdPricing />} />
            <Route path="pr-submissions" element={<PRSubmissions />} />
            <Route path="authors" element={<Authors />} />
            <Route path="write" element={<WriteAsAuthor />} />
          </Route>
        </Routes>
        </LanguageProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
